import {Component, DestroyRef, inject, OnInit, ViewEncapsulation} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {environment} from '@env';
import {ManagementApiService} from '@emm/management/services/management-api.service';
import {UserService} from '@emm/core/services/user.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {DialogService} from '@emm/shared/services/dialog.service';
import {filter, switchMap, tap} from 'rxjs/operators';
import * as RA from 'ramda-adjunct';
import {BehaviorSubject, defer, of} from 'rxjs';

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-cache-operations',
    styleUrls: ['./operations.component.scss'],
    templateUrl: './operations.component.html',
    standalone: false
})
export class OperationsComponent implements OnInit {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    cleared = new BehaviorSubject<boolean>(false);
    dataCopied = new BehaviorSubject<boolean>(false);
    protected readonly environment = environment;
    isProd = environment.name === 'prod';
    isStg = environment.name === 'stg';
    isAdminOrSuperAdmin = false;
    isSuperAdmin = false;

    constructor(private managementApi: ManagementApiService,
                private userService: UserService,
                private notificationService: NotificationService,
                private dialogService: DialogService,) {
        this.isAdminOrSuperAdmin = (this.userService.hasAdminRole() || this.userService.hasSuperAdminRole());
        this.isSuperAdmin = this.userService.hasSuperAdminRole();
    }

    ngOnInit(): void {}

    clearCache(): void {
        this.dialogService.openConfirmDialog('Are you sure you want to', 'Clear Cache', '')
            .afterClosed()
            .pipe(
                filter(RA.isTruthy),
                switchMap(() => this.managementApi.clearCache()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.cleared.next(true);
                    this.notificationService.success('Your cache was deleted!');
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                }
            });
    }

    copyProdData() {
        if (!this.isProd && !this.isStg && this.isAdminOrSuperAdmin) {
            return this.managementApi.copyProdData().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                next: () => {
                    this.dataCopied ? this.notificationService.success('Data copy request submitted!') :
                        this.notificationService.error('Data copy request cancelled!');
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                }
            });
        }
        if (this.isStg && this.isSuperAdmin) {
            return this.managementApi.copyProdData().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                next: () => {
                    this.dataCopied ? this.notificationService.success('Data copy request submitted!') :
                        this.notificationService.error('Data copy request cancelled!');
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                }
            });
        }
    }

    handleCopyProdDataClicked() {
        this.dialogService.openConfirmDialog('Are you sure you want to', 'Copy Prod Data', '')
            .afterClosed()
            .pipe(
                tap((result) => {
                    this.dataCopied.next(result);
                }),
                filter(RA.isTruthy),
                switchMap(() =>
                    defer(() => of(this.copyProdData()))
                ),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe();
    }
}
